import _extends from '@babel/runtime-corejs3/helpers/extends';
import _slicedToArray from '@babel/runtime-corejs3/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/objectWithoutProperties';
import _Promise from '@babel/runtime-corejs3/core-js-stable/promise';
import React__default, { forwardRef, useState, useCallback } from 'react';
import { debounce, throttle } from 'throttle-debounce';
import '../../hooks/8930fff059088aae845742b95b03a9c78f1349f3c58d1781f854d37c28181edd.cfe-internal.js';
import { useMenuContext } from '../NewMenu/parts/444d1f0fa255bfae713ca222a8af44d78ca3aaec1ca87f37ed69cc8c9e74934f.cfe-internal.js';
import '../Input/8b306eed80472bc167488d029758903f189ea21b58685c7d85ad4a1de9637088.cfe-internal.js';
import '../../utils/2d829def7915df1f35176a057ded5c77071616520fe7b6b8ef66f5eb29ca849f.cfe-internal.js';
import useMounted from '../../hooks/useMounted/1065327c06857f958b6ba8926f0ac9269f4f71ad07db9a879cc9f8e2f45f5255.cfe-internal.js';
import useLatest from '../../hooks/useLatest/1f51704b192f9e30a9862fa17b105c3f6454cca23cf8d6fb114db55de1c4e301.cfe-internal.js';
import { useEnhancedDidUpdate } from '../../hooks/useDidUpdate/f8ce5c85f293b5c4ca8be6bc5ac9f246003ac61cc91ab03b8814ccaab638a21a.cfe-internal.js';
import Input from '../Input/0313aac0a73c29ca9b860be5ca5b212107cd810318bb0b89176cc766dbf84b47.cfe-internal.js';
import mergeRefs from '../../utils/mergeRefs/836dcb860bec00d9c59bd64df8df6df42430840bc5bfc21b6000935baefbeacd.cfe-internal.js';

var _excluded = ["onSearch", "debouncePeriod", "throttlePeriod", "setIsLoading", "items", "setItems", "minChars", "value", "defaultValue", "onChange"];
var QuickSearchInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var onSearch = _ref.onSearch,
    _ref$debouncePeriod = _ref.debouncePeriod,
    debouncePeriod = _ref$debouncePeriod === void 0 ? 0 : _ref$debouncePeriod,
    _ref$throttlePeriod = _ref.throttlePeriod,
    throttlePeriod = _ref$throttlePeriod === void 0 ? 0 : _ref$throttlePeriod,
    setIsLoading = _ref.setIsLoading,
    _ref$items = _ref.items,
    items = _ref$items === void 0 ? [] : _ref$items,
    setItems = _ref.setItems,
    _ref$minChars = _ref.minChars,
    minChars = _ref$minChars === void 0 ? 1 : _ref$minChars,
    externalValue = _ref.value,
    _ref$defaultValue = _ref.defaultValue,
    defaultValue = _ref$defaultValue === void 0 ? '' : _ref$defaultValue,
    onChange = _ref.onChange,
    rest = _objectWithoutProperties(_ref, _excluded);
  var isMounted = useMounted();
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    internalValue = _useState2[0],
    setInternalValue = _useState2[1];
  var isControlled = externalValue !== undefined;
  var value = isControlled ? externalValue : internalValue;
  var valueRef = useLatest(value);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    changed = _useState4[0],
    setChanged = _useState4[1];
  var _ref2 = useMenuContext(),
    open = _ref2.isOpen,
    setIsOpen = _ref2.setIsOpen,
    internalCloseMenu = _ref2.closeAndFocusAnchor,
    anchorRef = _ref2.anchorRef,
    setHighlightedIndex = _ref2.setHighlightedIndex;
  var openMenu = function openMenu() {
    // this prevents the "no items to display" text from appearing on initial open.
    if (!open) {
      setIsLoading(true);
    }
    setIsOpen === null || setIsOpen === void 0 || setIsOpen(true);
  };
  var closeMenu = useCallback(function () {
    setItems([]);
    internalCloseMenu === null || internalCloseMenu === void 0 || internalCloseMenu();
    setIsLoading(true);
  }, [setItems, internalCloseMenu, setIsLoading]);
  var onSearchRef = useLatest(onSearch);
  var handleSearch = useCallback(function (queryValue) {
    if (!isMounted.current) {
      return;
    }
    setIsLoading(true);
    _Promise.resolve(onSearchRef.current && onSearchRef.current(queryValue)).then(function (newItems) {
      if (isMounted.current && queryValue === valueRef.current) {
        setItems(newItems);
        setIsLoading(false);
      }
    });
  }, [isMounted, onSearchRef, setIsLoading, valueRef, setItems]);
  var handleSearchThrottledDebounced = useCallback(debounce(debouncePeriod, throttle(throttlePeriod, handleSearch)), [debouncePeriod, throttlePeriod, handleSearch]);
  var handleChange = function handleChange(e) {
    setChanged(true);
    if (!isControlled) {
      setInternalValue(e.target.value);
    }
    if (typeof onChange === 'function') {
      onChange(e);
    }
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault(); // prevent scroll
      openMenu();
      setHighlightedIndex === null || setHighlightedIndex === void 0 || setHighlightedIndex(0);
      if (!open) {
        handleSearch(value);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); // prevent scroll
      openMenu();
      setHighlightedIndex === null || setHighlightedIndex === void 0 || setHighlightedIndex(items.length - 1);
      if (!open) {
        handleSearch(value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleChange({
        target: {
          value: ''
        }
      });
      if (open) {
        e.stopPropagation(); // prevent event from closing modals or popovers
        closeMenu();
      }
    } else if (e.key === 'Tab') {
      if (open) {
        closeMenu();
      }
    }
  };
  useEnhancedDidUpdate(function () {
    var shouldClose = value.length < minChars;
    if (changed) {
      setChanged(false);
      if (shouldClose) {
        setChanged(false);
        closeMenu();
      } else {
        openMenu();
        handleSearchThrottledDebounced(value);
      }
    }
  }, [handleSearchThrottledDebounced, minChars, value, openMenu, closeMenu, changed]);
  return (
    /*#__PURE__*/
    // @ts-ignore: Allow aria-expanded to be set to null to override the value set by Menu
    React__default.createElement(Input, _extends({}, rest, {
      "aria-expanded": null,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      ref: mergeRefs(ref, anchorRef),
      value: value
    }))
  );
});

export { QuickSearchInput as default };
